import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { Breadcrumbs } from '~/components/Breadcrumps';
import { Button } from '~/components/Button';
import { Trash2 } from 'lucide';
import { LucideIcon } from '~/components/LucideIcon';
import { CheckboxInput } from '~/components/Controls/Checkbox';
import { Input } from '~/components/Controls/Input';
import { TagsList } from '~/components/Controls/Tags';
import { SelectTags } from '~/components/Controls/Tags/api';
import { ToggleGroup } from '~/components/Controls/ToggleGroup';
import { useConfirm, useMessage } from '~/components/Dialogs';
import { onSubmitError } from '~/components/Form';
import { useModals } from '~/components/Modal';
import { Pagination } from '~/components/Pagination';
import { Progress } from '~/components/Progress';
import { TableRowActions, TableSort, TableSortDirection } from '~/components/Table';
import { Title } from '~/components/Title';
import { useAbility } from '~/lib/ability';
import { CSV } from '~/lib/csv';
import { renderBoolean, renderToggle } from '~/lib/helpers';
import { Intent } from '~/lib/intent';
import { notify } from '~/lib/notify';
import { sortByBoolean, sortByNumber, sortByString } from '~/lib/sort';
import { usePersistedSearch } from '~/lib/usePersistedSearch';
import { clientsLinks } from '~/modules/Clients';
import { SourcesAbility } from '~/modules/Sources/ability';
import { ISourcesFilter, prepareSourceSubmitData, fetchSource, toggleSource, createSource, updateSource, updateSourcesTags, deleteSource } from '~/modules/Sources/api';
import { useSourcesPageQuery, prefetchSourcesPage, useInvalidateSourcesPageQuery } from '~/modules/Sources/queries';
import { SourceForm } from '~/modules/Sources/forms/SourceForm';
import { SourcesForm } from '~/modules/Sources/forms/SourcesForm';
import { sourcesLinks } from '~/modules/Sources/links';
import style from '~/modules/Sources/index.module.scss';
import { defaultSource, ISource, ISourceSummary, ISourceTags } from '~/modules/Sources/models/Source';

const DEFAULT_PAGE_SIZE = 25;

const parseActive = (value: string | null): boolean | null =>
    value === 'true' ? true : value === 'false' ? false : null;

const applyBulkTagChanges = (ids: number[], tags: { addTags: string[]; removeTags: string[] }) =>
    Promise.all(ids.map(fetchSource))
        .then(fullSources => {
            const addSet = new Set(tags.addTags);
            const removeSet = new Set(tags.removeTags);
            const updated: ISourceTags[] = fullSources.map(source => ({
                sourceId: source.id,
                tags: [...new Set(source.tags ?? []).union(addSet).difference(removeSet)],
            }));
            return updateSourcesTags(updated);
        });

export const SourcesView = () => {
    const modal = useModals();
    const confirm = useConfirm();
    const message = useMessage();
    const ability = useAbility(SourcesAbility);
    const [, setSearchParams] = useSearchParams();
    const effectiveSearch = usePersistedSearch('sources.filter');
    const searchParams = useMemo(() => new URLSearchParams(effectiveSearch), [effectiveSearch]);

    const sourceSearch = searchParams.get('q') ?? '';
    const idSearch = searchParams.get('id') ?? '';
    const sellerSearch = searchParams.get('seller') ?? '';
    const tagFilter = searchParams.get('tag') ?? '';
    const active = parseActive(searchParams.get('active'));
    const page = Math.max(0, Number(searchParams.get('page') || '0'));

    const [sort, setSort] = useState({ field: 'id', direction: 'desc' });
    const [sourceInput, setSourceInput] = useState(sourceSearch);
    const [idInput, setIdInput] = useState(idSearch);
    const [sellerInput, setSellerInput] = useState(sellerSearch);

    const normalizedSource = useMemo(() => sourceInput.trim(), [sourceInput]);
    const normalizedId = useMemo(() => idInput.trim(), [idInput]);
    const normalizedSeller = useMemo(() => sellerInput.trim(), [sellerInput]);

    const prevSearchRef = useRef({ sourceSearch, idSearch, sellerSearch });
    if (prevSearchRef.current.sourceSearch !== sourceSearch && sourceInput === prevSearchRef.current.sourceSearch) {
        setSourceInput(sourceSearch);
    }
    if (prevSearchRef.current.idSearch !== idSearch && idInput === prevSearchRef.current.idSearch) {
        setIdInput(idSearch);
    }
    if (prevSearchRef.current.sellerSearch !== sellerSearch && sellerInput === prevSearchRef.current.sellerSearch) {
        setSellerInput(sellerSearch);
    }
    prevSearchRef.current = { sourceSearch, idSearch, sellerSearch };

    const setSearchParamsRef = useRef(setSearchParams);
    setSearchParamsRef.current = setSearchParams;

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setSearchParamsRef.current(prev => {
                const next = new URLSearchParams(prev);
                normalizedSource ? next.set('q', normalizedSource) : next.delete('q');
                normalizedId ? next.set('id', normalizedId) : next.delete('id');
                normalizedSeller ? next.set('seller', normalizedSeller) : next.delete('seller');
                next.delete('page');
                return next;
            }, { replace: true });
        }, 300);
        return () => window.clearTimeout(timeout);
    }, [normalizedSource, normalizedId, normalizedSeller]);

    const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
    const [checkAll, setCheckAll] = useState(false);
    const [refreshTags, setRefreshTags] = useState(false);

    const setUrlFilter = (changes: Record<string, string | null>) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            Object.entries(changes).forEach(([key, value]) => {
                value !== null ? next.set(key, value) : next.delete(key);
            });
            next.delete('page');
            return next;
        }, { replace: true });
    };

    const filter = useMemo<ISourcesFilter>(
        () => ({ sourceSearch, idSearch, sellerSearch, tagFilter, active }),
        [sourceSearch, idSearch, sellerSearch, tagFilter, active]
    );
    const pagination = useMemo(
        () => ({ pageNo: page, pageSize: DEFAULT_PAGE_SIZE, ...(sort.field === 'id' ? { sortBy: sort.direction } : {}) }),
        [page, sort]
    );

    const sourcesQuery = useSourcesPageQuery(filter, pagination);

    const pageResource = sourcesQuery.data ?? { items: [], count: 0, page, pageSize: DEFAULT_PAGE_SIZE };

    useEffect(() => {
        if (!sourcesQuery.data) return;
        const nextPage = pageResource.page + 1;
        if (nextPage * pageResource.pageSize >= pageResource.count) return;
        prefetchSourcesPage(filter, {pageNo: nextPage, pageSize: DEFAULT_PAGE_SIZE, ...( sort.field === 'id' ? { sortBy: sort.direction } : {}) });
    }, [sourcesQuery.data]);

    const refreshPagedSources = useInvalidateSourcesPageQuery();

    const sourceCreate = modal.create(SourceForm, {
        lockSeller: false,
        onSubmit: (source, formikActions, close) => {
            createSource(prepareSourceSubmitData(source))
                .then(() => { close(); refreshPagedSources(); })
                .catch(onSubmitError(formikActions));
        }
    });

    const sourceEdit = modal.create(SourceForm, {
        lockSeller: false,
        onSubmit: (source, formikActions, close) => {
            updateSource(prepareSourceSubmitData(source))
                .then(() => { close(); refreshPagedSources(); })
                .catch(onSubmitError(formikActions));
        }
    });

    const bulkItemsText = () => {
        const selected = (pageResource?.items ?? []).filter(r => checkedIds.has(r.id));
        return `Edit records (${selected.length}): ${selected.map(r => r.name || String(r.id)).join(', ')}`;
    };

    const sourcesEdit = modal.create(SourcesForm, {
        records: bulkItemsText(),
        onSubmit: (tags, formikActions, close) => {
            applyBulkTagChanges([...checkedIds], tags)
                .then(() => {
                    close();
                    setCheckedIds(new Set());
                    setCheckAll(false);
                    setRefreshTags(v => !v);
                    refreshPagedSources();
                })
                .catch(onSubmitError(formikActions));
        }
    });

    const handleEdit = (record: ISourceSummary) => {
        if (record?.id == null) return;
        fetchSource(record.id)
            .then(source => sourceEdit.show({ source }, { size: 'wide' }))
            .catch(error => notify.apiError(error, 'Failed to load source'));
    };

    const bulkActions = checkedIds.size > 1;

    const setCheck = (source: ISourceSummary) => {
        setCheckedIds(prev => {
            const next = new Set(prev);
            next.has(source.id) ? next.delete(source.id) : next.add(source.id);
            return next;
        });
    };

    const handleCheckAll = () => {
        const next = !checkAll;
        setCheckAll(next);
        setCheckedIds(next ? new Set((pageResource?.items ?? []).map(r => r.id)) : new Set());
    };

    const hasFilters = !!(sourceSearch || idSearch || sellerSearch || tagFilter || active !== null);

    const handleReset = () => {
        setSourceInput('');
        setIdInput('');
        setSellerInput('');
        setSearchParams({}, { replace: true });
    };

    const isPending = sourcesQuery.isFetching;

    return <div className={style.sources}>
        <div className='page-header-sticky'>
            <div className='page-header-breadcrumbs'>
                <Breadcrumbs />
            </div>
            <Title className='page-header-title' toolbar={<>
                {ability.canCreate() && <Button key='create' disabled={isPending} responsive intent={Intent.PRIMARY}
                    leftIcon={<i className='fa fa-plus' />}
                    onClick={() => sourceCreate.show({ source: { ...defaultSource } }, { size: 'wide' })}>Create</Button>}
                <Button key='reload' disabled={isPending} responsive intent={Intent.SUCCESS}
                    leftIcon={<i className='fas fa-sync-alt' />}
                    onClick={() => sourcesQuery.refetch()}>Reload</Button>
                {ability.canEdit() &&
                    <Button key='edit' disabled={isPending || !bulkActions} responsive intent={Intent.PRIMARY}
                        leftIcon={<i className='far fa-edit' />}
                        onClick={() => sourcesEdit.show()}>Edit</Button>}
                <Button responsive disabled={isPending}
                    onClick={() => CSV.fromDataSource(pageResource?.items ?? [], ability.canOnlyView()
                        ? ['id', 'name', 'tmax', 'active']
                        : ['id', 'name', 'sellerId', 'tmax', 'active']
                    ).download('sources.csv')}
                    leftIcon={<i className='far fa-file-excel' />}
                >Export</Button>
            </>}>Sources</Title>
        </div>
        <div className='layout-content-box'>
            {ability.canCreate() &&
                <form className={style.filter} onSubmit={e => e.preventDefault()}>
                    <div className={style.searchRow}>
                        <Input
                            value={sourceInput}
                            onChange={setSourceInput}
                            placeholder='Source name'
                            rightIcon={<i className='fa fa-search input-search-icon' />}
                        />
                        <Input
                            value={idInput}
                            onChange={setIdInput}
                            placeholder='Source ID'
                            rightIcon={<i className='fa fa-search input-search-icon' />}
                        />
                        <Input
                            value={sellerInput}
                            onChange={setSellerInput}
                            placeholder='Seller ID'
                            rightIcon={<i className='fa fa-search input-search-icon' />}
                        />
                        <SelectTags
                            refresh={refreshTags}
                            placeholder='Filter by Tag'
                            value={tagFilter}
                            clearable
                            onChange={val => setUrlFilter({ tag: val || null })}
                        />
                        <ToggleGroup
                            ariaLabel='Status'
                            items={[
                                { label: 'All', value: null },
                                { label: 'Active', value: true },
                                { label: 'Inactive', value: false }
                            ]}
                            value={active}
                            disabled={isPending}
                            onChange={val => setUrlFilter({ active: val !== null ? String(val) : null })}
                        />
                        {hasFilters && (
                            <Button
                                responsive
                                onClick={handleReset}
                                leftIcon={<i className='fa fa-times' />}
                            >Reset</Button>
                        )}
                    </div>
                </form>
            }
            <TableSort records={pageResource?.items ?? []}
                header
                defaultSort={{ sort: sortByNumber, field: 'id', direction: TableSortDirection.DESC }}
                onSortChange={({ field, direction, backendSort }) => {
                    if (backendSort) {
                        setSort({ field, direction: direction === TableSortDirection.ASC ? 'asc' : 'desc' });
                    }
                }}
                columns={[
                    {
                        field: 'check',
                        header: <CheckboxInput value={checkAll} onChange={handleCheckAll} className='placeholder' variant='primary' />,
                        small: true,
                        hidden: ability.canOnlyView(),
                        cell: source => <CheckboxInput
                            value={checkedIds.has((source as unknown as ISourceSummary).id)}
                            onChange={() => setCheck(source as unknown as ISourceSummary)}
                            className='placeholder' />
                    },
                    {
                        field: 'id',
                        header: 'ID',
                        sort: sortByNumber,
                        backendSort: true,
                        small: true
                    }, {
                        field: 'name',
                        header: 'Name',
                        sort: sortByString,
                        cell: source => <NavLink to={sourcesLinks.source(source.id)}>
                            {(source as unknown as ISourceSummary).name}
                        </NavLink>
                    }, {
                        field: 'sellerId',
                        header: 'Seller ID',
                        hidden: ability.canOnlyView(),
                        sort: sortByNumber,
                        responsive: true,
                        cell: source => <NavLink to={clientsLinks.seller((source as unknown as ISourceSummary).sellerId)}>
                            {(source as unknown as ISourceSummary).sellerId}
                        </NavLink>
                    }, {
                        field: 'tags',
                        header: 'Tags',
                        hidden: ability.canOnlyView(),
                        cell: source => <TagsList values={(source as unknown as ISourceSummary).tags} size='small' />,
                        short: true
                    }, {
                        field: 'tmax',
                        header: 'TMax',
                        hidden: ability.canOnlyView(),
                        sort: sortByNumber
                    }, {
                        field: 'active',
                        header: 'Active',
                        cell: source => ability.canEdit() ? renderToggle(
                            (source as unknown as ISourceSummary).active,
                            () => confirm(`${(source as unknown as ISourceSummary).active ? 'Dea' : 'A'}ctivate source?`, {
                                onOk: () => toggleSource(source as unknown as ISource, !(source as unknown as ISourceSummary).active)
                                    .then(() => refreshPagedSources())
                                    .catch(() => message(
                                        `Can't ${(source as unknown as ISourceSummary).active ? 'dea' : 'a'}ctivate source`,
                                        { intent: Intent.DANGER, icon: <i className='fa fa-times' /> }
                                    )),
                                icon: <i className='fa fa-question' />,
                                intent: Intent.WARNING
                            })
                        ) : renderBoolean((source as unknown as ISourceSummary).active),
                        sort: sortByBoolean
                    }, {
                        small: true,
                        cell: source => <TableRowActions>
                            {ability.canEdit() && (
                                <Button small variant='menu'
                                    onClick={() => handleEdit(source as unknown as ISourceSummary)}
                                    leftIcon={<i className='fa fa-edit' />}>Edit</Button>
                            )}
                            {ability.canDelete() && (
                                <Button small variant='menu' intent={Intent.DANGER}
                                    onClick={() => confirm('Delete source?', {
                                        onOk: () => deleteSource(source as unknown as ISource)
                                            .then(() => refreshPagedSources())
                                            .catch(() => message('Failed to delete source', { intent: Intent.DANGER })),
                                        icon: <LucideIcon icon={Trash2} size={20} />,
                                        intent: Intent.DANGER
                                    })}
                                    leftIcon={<i className='fa fa-trash' />}>Delete</Button>
                            )}
                        </TableRowActions>
                    }
                ]}
                placeholder={isPending && <Progress />}
            />
            {pageResource.count > pageResource.pageSize &&
                <Pagination
                    padding={4}
                    offset={pageResource.pageSize * pageResource.page}
                    limit={pageResource.pageSize}
                    total={pageResource.count}
                    onChange={(newPage) => {
                        setSearchParams(prev => {
                            const next = new URLSearchParams(prev);
                            newPage > 0 ? next.set('page', String(newPage)) : next.delete('page');
                            return next;
                        }, { replace: true });
                        setCheckedIds(new Set());
                        setCheckAll(false);
                    }}
                />
            }
        </div>
    </div>;
};
